/**
 * Pre-onboarding intake — the golden-source customer read (SPEC-14 §5, S14-5).
 *
 * Wire contract (additive this release):
 *   GET  /api/v1/sessions/{id}/intake            → { fields: [IntakeField…] }
 *        The CUSTOMER projection of the session's golden-source record —
 *        HIDDEN fields are filtered SERVER-side and never reach this client.
 *        404 / absent route = no intake for this session OR an older be —
 *        both mean the confirm step is skipped entirely (honest absence).
 *   POST /api/v1/sessions/{id}/intake:confirm    → body { values }
 *        Records the customer's chained confirm (`intake.confirmed`, template
 *        version + content hash server-side). `values` carries ONLY the
 *        CUSTOMER_COMPLETABLE blanks the customer filled — a customer-typed
 *        value NEVER overwrites a golden field (SPEC-14 §5; a 4xx names the
 *        problem). 409 = already confirmed — treated as done, not an error.
 *
 * Tamper honesty: `IMMUTABLE_DISPLAY` fields render read-only; an unknown or
 * absent classification is treated as read-only too — failing SAFE means the
 * customer can never edit a field the template did not open to them.
 */

import { ApiError, isMockMode, request } from './client';

/** How the droom may treat a field (SPEC-14 §2 tamper_policy / provenance). */
export type IntakeFieldMode = 'IMMUTABLE_DISPLAY' | 'CUSTOMER_COMPLETABLE';

export interface IntakeField {
  readonly key: string;
  /** Rendering label from the template; the key humanises as fallback. */
  readonly label: string;
  /** The golden value, or null when the field is an open blank. */
  readonly value: string | null;
  readonly mode: IntakeFieldMode;
}

/** Humanise a snake_case template key for a row missing its label. */
export function humaniseIntakeKey(key: string): string {
  const words = key.toLowerCase().split(/[_\s]+/).filter(Boolean).join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}

/**
 * Normalise one wire field. Classification is read tolerantly (`mode` /
 * `tamperPolicy` / `provenanceClass` spellings) and ANYTHING that is not
 * explicitly CUSTOMER_COMPLETABLE renders read-only (fail-safe direction).
 */
export function parseIntakeField(raw: unknown): IntakeField | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const key = typeof r.key === 'string' && r.key.trim() !== '' ? r.key.trim() : null;
  if (!key) return null;
  const label =
    typeof r.label === 'string' && r.label.trim() !== ''
      ? r.label.trim()
      : humaniseIntakeKey(key);
  const rawValue = r.value;
  const value =
    typeof rawValue === 'string'
      ? rawValue
      : typeof rawValue === 'number' || typeof rawValue === 'boolean'
        ? String(rawValue)
        : null;
  const classification = [r.mode, r.tamperPolicy, r.tamper_policy, r.provenanceClass, r.provenance_class]
    .map((v) => (typeof v === 'string' ? v.toUpperCase() : ''))
    .join(' ');
  const mode: IntakeFieldMode = classification.includes('CUSTOMER_COMPLETABLE')
    ? 'CUSTOMER_COMPLETABLE'
    : 'IMMUTABLE_DISPLAY';
  return { key, label, value, mode };
}

/**
 * The session's customer-facing intake fields, or null when the session has
 * none / the be predates the route (both = skip the confirm step). Fail-soft
 * by directive: ANY read failure resolves null — an intake blip must never
 * strand the flow (the be's chained record, not this client, is the evidence).
 */
export async function getSessionIntake(sessionId: string): Promise<IntakeField[] | null> {
  if (isMockMode()) return null; // no intake plane exists in mock — honest absence
  try {
    const res = await request<{ fields?: unknown }>(
      `/api/v1/sessions/${encodeURIComponent(sessionId)}/intake`,
    );
    const rawFields = Array.isArray(res.fields) ? res.fields : null;
    if (!rawFields || rawFields.length === 0) return null;
    const fields = rawFields
      .map(parseIntakeField)
      .filter((f): f is IntakeField => f !== null);
    return fields.length > 0 ? fields : null;
  } catch {
    return null; // 404 (older be / no intake) and transient faults alike: skip honestly
  }
}

/**
 * Record the customer's confirm. `values` = the CUSTOMER_COMPLETABLE blanks
 * they filled (possibly empty — confirming untouched golden fields is itself
 * the act). 409 = already confirmed, treated as success. Other failures throw
 * for the caller to render honestly with a retry.
 */
export async function confirmSessionIntake(
  sessionId: string,
  values: Record<string, string>,
): Promise<void> {
  if (isMockMode()) return;
  try {
    await request<unknown>(
      `/api/v1/sessions/${encodeURIComponent(sessionId)}/intake:confirm`,
      { method: 'POST', body: { values } },
    );
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) return; // already confirmed — done
    // A 2xx with an empty body fails request()'s unconditional res.json() as a
    // SyntaxError — that is a SUCCESS (the server accepted; there was just no
    // body). HTTP refusals are ApiError and real network faults are TypeError;
    // both rethrow for the caller's honest error + retry.
    if (e instanceof SyntaxError) return;
    throw e;
  }
}
